import {
  Section,
  SectionBody,
  SectionDescription,
  SectionFooter,
  SectionHeader,
  SectionIcon,
  SectionTitle,
  SectionTitleRow,
} from '@campoint/odi-ui';
import { Textsms } from '@campoint/odi-ui-icons';
import { Button, Divider, VStack } from '@chakra-ui/react';
import { FormikConfig, FormikProvider, useFormik } from 'formik';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SectionCenterContainer } from '../../../../../components/Layout/SectionCenterContainer';
import { SectionDivider } from '../../../../../components/Layout/SectionDivider';
import { multiTextareaDataForBackendV2 } from '../../../../../hooks/useMultiTextareaData';
import { InterviewProgressCard } from '../../../components/InterviewProgressCard/InterviewProgressCard';
import { MultiLangFieldV2 } from '../../../components/field/MultiLangFieldV2';
import { useModelProfile } from '../../../provider/ModelProfileProvider';
import {
  extractDirtyValues,
  extractInitialValuesFromFields,
  extractValidationSchema,
} from '../../../provider/utils';
import { useFieldInitialErrors, useFieldInitialTouched } from '../../utils';

export const ModelProfileInterviewSection: React.FC = () => {
  const minimumCompletedInterviewQuestions = 3;

  const [t] = useTranslation(['language', 'modelProfile']);
  const unknownError = t('modelProfile:fields.unknownError.unknownError');

  const {
    interviewCollection,
    interviewCollectionError,
    isSaveDataLoading,
    actions: { saveProfileData, setIsDataDirty },
  } = useModelProfile();

  const completeness = interviewCollection?.completeness;

  const fields = useMemo(
    () => interviewCollection?.fields ?? {},
    [interviewCollection]
  );

  const initialErrors = useFieldInitialErrors(
    interviewCollectionError,
    unknownError
  );

  const initialTouched = useFieldInitialTouched(interviewCollectionError);

  const {
    modelInterviewPartner,
    modelInterviewIsland,
    modelInterviewEnterprise,
    modelInterviewHobbies,
    modelInterviewSexualPosition,
    modelInterviewSexualFantasies,
    modelInterviewSexNotOk,
    modelInterviewSexOk,
    modelInterviewEroticExperience,
  } = fields;

  const initialValues = useMemo(
    () => extractInitialValuesFromFields(fields),
    [fields]
  );

  const onSubmitHandler = useCallback<
    FormikConfig<typeof initialValues>['onSubmit']
  >(
    async (values) => {
      if (isSaveDataLoading) {
        return;
      }

      const modelInterviewData = multiTextareaDataForBackendV2({
        values,
        fields,
      });

      const dirtyValues = extractDirtyValues(modelInterviewData, initialValues);

      saveProfileData({
        interviewData: dirtyValues,
      }).then();
    },
    [saveProfileData, isSaveDataLoading, initialValues, fields]
  );

  const validationSchema = React.useMemo(
    () =>
      extractValidationSchema(fields, {
        modelInterviewPartner: {
          isOptional: true,
        },
        modelInterviewIsland: {
          isOptional: true,
        },
        modelInterviewEnterprise: {
          isOptional: true,
        },
        modelInterviewHobbies: {
          isOptional: true,
        },
        modelInterviewSexualPosition: {
          isOptional: true,
        },
        modelInterviewSexualFantasies: {
          isOptional: true,
        },
        modelInterviewSexNotOk: {
          isOptional: true,
        },
        modelInterviewSexOk: {
          isOptional: true,
        },
        modelInterviewEroticExperience: {
          isOptional: true,
        },
      }),
    [fields]
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    //@ts-ignore
    initialErrors,
    initialTouched,
    onSubmit: onSubmitHandler,
    enableReinitialize: true,
  });

  useEffect(() => {
    setIsDataDirty(formik.dirty);
    return () => {
      setIsDataDirty(false);
    };
  }, [formik.dirty, setIsDataDirty]);

  return (
    <Section aria-busy={isSaveDataLoading}>
      <SectionHeader>
        <SectionCenterContainer>
          <SectionTitleRow>
            <SectionIcon as={Textsms} />
            <SectionTitle fontSize={'xl'}>
              {t('modelProfile:heading.Interview')}
            </SectionTitle>
          </SectionTitleRow>
          <SectionDescription>
            {t(
              'modelProfile:text.DasInterviewGibtBesuchernDeinesProfilsEinAusfuhrlichesBildVonDirSoStXX'
            )}
          </SectionDescription>
        </SectionCenterContainer>
      </SectionHeader>
      <SectionDivider isWidthRestricted />
      <SectionBody>
        <SectionCenterContainer>
          <FormikProvider value={formik}>
            <VStack spacing={8} divider={<Divider size={'s'} />}>
              {completeness?.complete !== undefined &&
              completeness?.complete < minimumCompletedInterviewQuestions ? (
                <InterviewProgressCard
                  data-testid={'interviewProgressCard'}
                  requiredCount={minimumCompletedInterviewQuestions}
                  fulfilledCount={completeness?.complete}
                  borderColor={'indigo.200'}
                />
              ) : null}
              <MultiLangFieldV2
                field={modelInterviewPartner}
                label={t('modelProfile:text.WieSollteDeinTraumpartnerAussehen')}
                maxCharacterCount={modelInterviewPartner?.maxLength ?? 250}
              />
              <MultiLangFieldV2
                field={modelInterviewIsland}
                label={t(
                  'modelProfile:text.WasWurdestDuAufEineEinsameInselMitnehmen'
                )}
                maxCharacterCount={modelInterviewIsland?.maxLength ?? 250}
              />
              <MultiLangFieldV2
                field={modelInterviewEnterprise}
                label={t('modelProfile:text.WasWolltestDuSchonImmerMalMachen')}
                maxCharacterCount={modelInterviewEnterprise?.maxLength ?? 250}
              />
              <MultiLangFieldV2
                field={modelInterviewHobbies}
                label={t(
                  'modelProfile:text.WasMachstDuWennDuGeradeNichtOnlineBist'
                )}
                maxCharacterCount={modelInterviewHobbies?.maxLength ?? 250}
              />
              <MultiLangFieldV2
                field={modelInterviewSexualPosition}
                label={t('modelProfile:text.WasIstDeineLieblingsstellung')}
                maxCharacterCount={
                  modelInterviewSexualPosition?.maxLength ?? 250
                }
              />
              <MultiLangFieldV2
                field={modelInterviewSexualFantasies}
                label={t('modelProfile:text.WelcheSexphantasienHastDu')}
                maxCharacterCount={
                  modelInterviewSexualFantasies?.maxLength ?? 250
                }
              />
              <MultiLangFieldV2
                field={modelInterviewSexNotOk}
                label={t('modelProfile:text.WasMagstDuBeimSexUberhauptNicht')}
                maxCharacterCount={modelInterviewSexNotOk?.maxLength ?? 250}
              />
              <MultiLangFieldV2
                field={modelInterviewSexOk}
                label={t('modelProfile:text.WasMagstDuBeimSex')}
                maxCharacterCount={modelInterviewSexOk?.maxLength ?? 250}
              />
              <MultiLangFieldV2
                field={modelInterviewEroticExperience}
                label={t(
                  'modelProfile:text.WasWarDeinAufregendstesErotischesErlebnis'
                )}
                maxCharacterCount={
                  modelInterviewEroticExperience?.maxLength ?? 250
                }
              />
            </VStack>
          </FormikProvider>
        </SectionCenterContainer>
      </SectionBody>
      <SectionDivider isWidthRestricted />
      <SectionFooter>
        <SectionCenterContainer>
          <Button
            alignSelf={'center'}
            onClick={() => formik.submitForm()}
            children={t('modelProfile:button.SpeichernVeroffentlichen')}
            isDisabled={!(formik.isValid && formik.dirty)}
            isLoading={formik.isSubmitting || isSaveDataLoading}
          />
        </SectionCenterContainer>
      </SectionFooter>
    </Section>
  );
};
